import { useFormik } from "formik";
import { Box, Fade, Button, Alert, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useState, useRef, useEffect, useCallback } from "react";
import RecaptchaV2Host from "../../../components/RecaptchaV2Host";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { appColors } from "../../../constants/theme";
import {
  EmailFormContainer,
  EmailFormFieldContainer,
  PasswordFormFieldContainer,
  StyledEmailTextField,
  StyledPasswordTextField,
  VerificationButton,
  PasswordVisibilityButton,
  ForgotPasswordContainer,
  ForgotPasswordButton,
  RecaptchaContainer
} from "../style";

const EmailForm = ({ handleLogin, loading,
  showCaptcha = false,
  siteKey,
  onCaptcha,
  isVerified,
  onResendVerification,
  showVerificationButton = false,
  onEmailChange,
  resetPasswordCounter,
  loginErrorKey,
  onCredentialEdit,
  superLogin,
  onForgotPassword,
  onFormValuesChange,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  /** Başarısız giriş / süre doldu → RecaptchaV2Host yeniden mount */
  const [recaptchaMountKey, setRecaptchaMountKey] = useState(0);
  const prevResetPasswordCounter = useRef(resetPasswordCounter);
  //VALIDATION SCHEMA-------------------
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("invalid_email_address"))
      .max(320, t("maximum_letters_warning", { item: 320 }))
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        t("invalid_email_address")
      )
      .required(t("email_required")),
    password: Yup.string()
      .min(8, t("minimum_letters_warning", { item: 8 }))
      .max(32, t("maximum_letters_warning", { item: 32 }))
      .matches(
        /^[a-zA-Z0-9!@#$%^&*()\-_,.?":{}|<>]+$/,
        t("password_requirements")
      )
      .required(t("password_required")),
  });

  const { handleSubmit, handleChange, handleBlur, values, touched, errors, setFieldValue, setFieldTouched } =
    useFormik({
      initialValues: {
        email: superLogin ? "zenam_admin@doctorapp.com" : "",
        password: superLogin ? "A1234567" : "",
      },
      validationSchema,
      validateOnBlur: true,
      onSubmit: (values) => {
        console.log('🔍 [EmailForm] Formik onSubmit çağrıldı:', values);
        console.log('🔍 [EmailForm] handleLogin fonksiyonu çağrılıyor');
        handleLogin(values);
      },
    });

  // Login başarısız olduğunda şifre alanını temizlemek için tetikleyici
  useEffect(() => {
    // Başarısız login denemelerinden sonra şifre alanını sıfırla
    if (typeof resetPasswordCounter === 'number' && resetPasswordCounter > 0) {
      setFieldValue('password', '');
      setFieldTouched('password', false, false);
    }
  }, [resetPasswordCounter, setFieldValue, setFieldTouched]);

  // Form değerleri değiştiğinde Captcha gösterimini kontrol et
  useEffect(() => {
    if (onFormValuesChange) {
      onFormValuesChange(values.email, values.password);
    }
  }, [values.email, values.password, onFormValuesChange]);

  useEffect(() => {
    if (prevResetPasswordCounter.current !== resetPasswordCounter) {
      prevResetPasswordCounter.current = resetPasswordCounter;
      if (showCaptcha) {
        setRecaptchaMountKey((k) => k + 1);
      }
    }
  }, [resetPasswordCounter, showCaptcha]);

  const remountRecaptcha = useCallback(() => {
    setRecaptchaMountKey((k) => k + 1);
  }, []);

  return (
    <form
      onSubmit={(e) => {
        console.log('🔍 [EmailForm] Form submit event tetiklendi');
        e.preventDefault();
        handleSubmit(e);
      }}
    >
      <EmailFormContainer>
        <Fade in timeout={600}>
          <Box>
            <EmailFormFieldContainer>
              <StyledEmailTextField
                variant="outlined"
                id="email"
                name="email"
                label={t("email")}
                inputProps={{
                  maxLength: 320,
                }}
                fullWidth
                value={values.email}
                onChange={(e) => {
                  handleChange(e);
                  onCredentialEdit && onCredentialEdit();
                  onEmailChange && onEmailChange(); // Email değişince verification butonunu gizle
                }}
                onBlur={handleBlur}
                error={Boolean(errors.email) && touched.email}
                helperText={touched.email && errors.email}
                InputProps={{
                  startAdornment: (
                    <Email sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                  ),
                  endAdornment: showVerificationButton ? (
                    <VerificationButton
                      size="small"
                      variant="text"
                      onClick={() => onResendVerification && onResendVerification(values.email)}
                      disabled={loading}
                    >
                      {loading ? "..." : t("verify")}
                    </VerificationButton>
                  ) : undefined,
                }}
              />
            </EmailFormFieldContainer>
            <PasswordFormFieldContainer>
              <StyledPasswordTextField
                variant="outlined"
                id="password"
                name="password"
                label={t("password")}
                inputProps={{
                  maxLength: 32,
                }}
                fullWidth
                value={values.password}
                onChange={(e) => {
                  handleChange(e);
                  onCredentialEdit && onCredentialEdit();
                }}
                onBlur={handleBlur}
                error={Boolean(errors.password) && touched.password}
                helperText={touched.password && errors.password}
                type={passwordVisibility ? "text" : "password"}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSubmit();
                }}
                InputProps={{
                  startAdornment: (
                    <Lock sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                  ),
                  endAdornment: (
                    <PasswordVisibilityButton
                      onClick={() => setPasswordVisibility((x) => !x)}
                    >
                      {passwordVisibility ? <VisibilityOff /> : <Visibility />}
                    </PasswordVisibilityButton>
                  ),
                }}
              />
            </PasswordFormFieldContainer>
            {!superLogin && (
              <ForgotPasswordContainer>
                <ForgotPasswordButton
                  variant="text"
                  onClick={() => onForgotPassword ? onForgotPassword() : navigate("/forgot-password")}
                >
                  {t("forgot_password")}
                </ForgotPasswordButton>
              </ForgotPasswordContainer>
            )}
          </Box>
        </Fade>
        {loginErrorKey ? (
          <Alert severity="error" sx={{ mt: 2, borderRadius: "12px" }}>
            {t(loginErrorKey)}
          </Alert>
        ) : null}
        {showCaptcha && (
          <RecaptchaContainer>
            <RecaptchaV2Host
              siteKey={siteKey}
              hl="tr"
              resetSignal={recaptchaMountKey}
              onChange={onCaptcha}
              onExpired={remountRecaptcha}
              onError={remountRecaptcha}
            />
          </RecaptchaContainer>
        )}

        {/* Login Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={(() => {
              // Form validasyonu kontrolü
              const hasEmailError = Boolean(errors.email && touched.email);
              const hasPasswordError = Boolean(errors.password && touched.password);
              
              // Email ve şifre geçerliliği kontrolü (validation schema'ya göre)
              const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
              const isValidEmail = values.email && emailRegex.test(values.email);
              const isValidPassword = values.password && values.password.length >= 8;
              
              // Form geçersizse: hata var VEYA email/şifre geçersiz VEYA boş
              const isFormInvalid = hasEmailError || hasPasswordError || !isValidEmail || !isValidPassword;
              
              // Captcha kontrolü
              const isCaptchaRequired = showCaptcha && !isVerified;
              
              const isDisabled = loading || isFormInvalid || isCaptchaRequired;
              console.log('🔍 [EmailForm] Button disabled check:', {
                loading,
                hasEmailError,
                hasPasswordError,
                isValidEmail,
                isValidPassword,
                isFormInvalid,
                showCaptcha,
                isVerified,
                isCaptchaRequired,
                isDisabled
              });
              return isDisabled;
            })()}
            sx={{
              background: theme.palette.primary.main,
              borderRadius: "12px",
              padding: "0.875rem",
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              marginTop: "1rem",
              "&:hover": {
                background: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? t("logging_in") : t("login_button")}
          </Button>
        </EmailFormContainer>
    </form>
  );
};

export default EmailForm;
